import { createAuthClient, type SessionType, type TokenStorageAdapter } from "@shared/feShared/auth/tokenExchange";
import {
  clearAuthLocalStorage,
  getSessionTypeFromLocalStorage,
  getTokenFromLocalStorage,
  setSessionTypeToLocalStorage,
  setTokenToLocalStorage,
} from "@/lib/storage";

const LOGIN_PATH = "/login";

/**
 * Web token storage adapter.
 *
 * - Refresh tokens are not handled here on web; they live in an HttpOnly
 *   cookie set by the backend and travel with `credentials: "include"`.
 * - The access token is persisted only for LEGACY (non-refreshable) sessions
 *   so it survives a page refresh. Cognito access tokens are never persisted
 *   — after a refresh the first API call will trigger a cookie-based refresh.
 */
const webTokenStorage: TokenStorageAdapter = {
  getRefreshToken: () => Promise.resolve(null),
  setRefreshToken: () => Promise.resolve(),
  getAccessToken: () => Promise.resolve(getTokenFromLocalStorage()),
  setAccessToken: (token) => {
    setTokenToLocalStorage(token);
    return Promise.resolve();
  },
  // Session-type marker. On web the refresh token lives in an HttpOnly
  // cookie invisible to JS, so without this marker `hydrate()` can't tell
  // a booted Cognito user from a logged-out one on cold reload.
  getSessionType: () => Promise.resolve(getSessionTypeFromLocalStorage()),
  setSessionType: (type) => {
    setSessionTypeToLocalStorage(type);
    return Promise.resolve();
  },
  clearTokens: () => {
    setTokenToLocalStorage(null);
    setSessionTypeToLocalStorage(null);
    return Promise.resolve();
  },
};

// Seed persisted auth state synchronously. `localStorage` is sync on web,
// so we can avoid the async `hydrate()` race between the first outbound
// request and storage reads on page refresh. A persisted access token is
// always a legacy (non-refreshable) one — Cognito access tokens are never
// written to localStorage.
const persistedAccessToken = getTokenFromLocalStorage();
const persistedSessionType = getSessionTypeFromLocalStorage();

const isOnLoginPage = (): boolean => window.location.pathname === LOGIN_PATH;

const initialSessionType: SessionType | null =
  persistedSessionType ?? (persistedAccessToken ? "non-refreshable" : null);

const authClient = createAuthClient({
  baseUrl: import.meta.env.VITE_API_DOMAIN || "",
  credentials: "include",
  storage: webTokenStorage,
  onSessionExpired: () => {
    clearAuthLocalStorage();
    if (!isOnLoginPage()) {
      window.location.replace(LOGIN_PATH);
    }
  },
  initialAccessToken: persistedAccessToken,
  initialSessionType,
});

/**
 * Cognito refresh-on-boot promise.
 *
 * Cognito access tokens are intentionally not persisted (only the HttpOnly
 * refresh cookie survives a page refresh). That leaves a window after reload
 * where the session type is "refreshable" but we have no access token in
 * memory yet.
 *
 * Letting the first GraphQL query fly with an empty `Authorization` header
 * works in theory — the server returns 401 and our errorLink retries after
 * a refresh — but in practice multiple parallel queries (Apollo fires several
 * on a fresh dashboard mount) all hit `loading: false` with empty data
 * during the brief refresh window, and route guards / data watchers
 * misinterpret that as "user has no data" and force a logout.
 *
 * To eliminate the race we kick off a single proactive `/token-refresh/`
 * call here. The bootstrapLink in apollo-client.ts gates ALL outbound
 * queries on this promise, so the very first request already carries a
 * valid Bearer token. If the refresh fails (cookie expired/missing),
 * `onSessionExpired` above handles the redirect.
 */
let bootstrapPromise: Promise<unknown> | null = null;
if (initialSessionType === "refreshable" && !persistedAccessToken && !isOnLoginPage()) {
  bootstrapPromise = authClient.refresh().catch(() => null);
}

export const getBootstrapPromise = (): Promise<unknown> | null => bootstrapPromise;

export const clearBootstrapPromise = (): void => {
  bootstrapPromise = null;
};

export { authClient };
